export interface ApiError {
	code: string;
	message: string;
}

export interface ApiResponseOfAuthResponse {
	data: null | AuthResponse;
	error: null | ApiError;
}

export interface ApiResponseOfBlogArticleDto {
	data: null | BlogArticleDto;
	error: null | ApiError;
}

export interface ApiResponseOfBlogArticlePageDto {
	data: null | BlogArticlePageDto;
	error: null | ApiError;
}

export interface ApiResponseOfFeedbackDto {
	data: null | FeedbackDto;
	error: null | ApiError;
}

export interface ApiResponseOfFurnitureItemDto {
	data: null | FurnitureItemDto;
	error: null | ApiError;
}

export interface ApiResponseOfIReadOnlyListOfFurnitureItemDto {
	data: FurnitureItemDto[] | null;
	error: null | ApiError;
}

export interface ApiResponseOfIReadOnlyListOfProjectDto {
	data: ProjectDto[] | null;
	error: null | ApiError;
}

export interface ApiResponseOfIReadOnlyListOfUserDto {
	data: UserDto[] | null;
	error: null | ApiError;
}

export interface ApiResponseOfImageUploadDto {
	data: null | ImageUploadDto;
	error: null | ApiError;
}

export interface ApiResponseOfProjectDataDto {
	data: null | ProjectDataDto;
	error: null | ApiError;
}

export interface ApiResponseOfProjectDto {
	data: null | ProjectDto;
	error: null | ApiError;
}

export interface ApiResponseOfUserDto {
	data: null | UserDto;
	error: null | ApiError;
}

export interface AuthResponse {
	accessToken: string;
	refreshToken: string;
	user: UserDto;
}

export interface BlogArticleDto {
	content: JsonElement;
	/** @format date-time */
	createdAt: string;
	excerpt: string;
	/** @format uuid */
	id: string;
	slug: string;
	title: string;
	/** @format date-time */
	updatedAt: string;
}

export interface BlogArticlePageDto {
	hasMore: boolean;
	items: BlogArticleDto[];
}

export interface BlogArticleRequest {
	content: JsonElement;
	excerpt: string;
	slug: string;
	title: string;
}

export interface ConfirmEmailRequest {
	code: string;
	email: string;
}

export interface CreateProjectRequest {
	description: string | null;
	name: string;
}

export interface FeedbackDto {
	/** @format date-time */
	createdAt: string;
	/** @format uuid */
	id: string;
}

export interface FeedbackRequest {
	text: string;
	topics: FeedbackTopicEnum[];
}

/** @format int32 */
export enum FeedbackTopicEnum {
	Plan2D = 0,
	View3D = 1,
	Elevation = 2,
	Performance = 3,
	CatalogExpansion = 4,
	MoreMaterials = 5,
	Interface = 6,
	Other = 7,
}

/** @format int32 */
export enum FrontHinge {
	Left = 0,
	Right = 1,
}

export interface FurnitureData {
	kitchen: (null | KitchenData) | null;
	module: (null | ModuleData) | null;
	name: string;
	wardrobe: (null | WardrobeData) | null;
}

export interface FurnitureItemDto {
	/** @format date-time */
	createdAt: string;
	data: FurnitureData;
	/** @format uuid */
	id: string;
	/** @format uuid */
	projectId: string | null;
	/** @format date-time */
	updatedAt: string;
}

export interface FurnitureItemRequest {
	data: FurnitureData;
	/** @format uuid */
	id: string | null;
	/** @format uuid */
	projectId: string | null;
}

/** @format int32 */
export enum FurnitureModuleType {
	Floor = 0,
	Wall = 1,
	Tall = 2,
	Shelving = 3,
}

export type IFormFile = string;

export interface ImageUploadDto {
	url: string;
}

export type JsonElement = Record<string, unknown>;

export interface KitchenCellData {
	fill: KitchenCellFill;
	/** @format int32 */
	height: number;
	id: string;
	/** @format int32 */
	shelves: number | null;
}

/** @format int32 */
export enum KitchenCellFill {
	Drawer = 0,
	Door = 1,
	Open = 2,
}

export interface KitchenData {
	apronColor: string | null;
	bodyColor: string | null;
	bottomOven: boolean | null;
	column: boolean;
	cornerSide: KitchenSide;
	defaultDoorType: null | KitchenDoorType;
	/** @format int32 */
	defaultShelves: number | null;
	dishwasher: boolean;
	facadeStyle: null | KitchenFacadeStyle;
	fridge: boolean;
	/** @format int32 */
	fridgeHeight: number | null;
	frontColor: string;
	handleStyle: null | KitchenHandleStyle;
	/** @format int32 */
	height: number;
	island: boolean;
	/** @format int32 */
	islandOverhang: number | null;
	/** @format int32 */
	islandSideOverhang: number | null;
	layout: KitchenLayout;
	lengths: KitchenLengthsData;
	sections: Record<string, KitchenRunSectionsData> | null;
	/** @format int32 */
	upperBottom: number | null;
	uppers: boolean;
	worktopColor: string;
	/** @format int32 */
	worktopHeight: number | null;
}

/** @format int32 */
export enum KitchenDoorType {
	Left = 0,
	Right = 1,
	Double = 2,
	Lift = 3,
	Flap = 4,
	Drawer = 5,
	None = 6,
}

/** @format int32 */
export enum KitchenFacadeStyle {
	Flat = 0,
	Shaker = 1,
	Glass = 2,
}

export interface KitchenFrontSettingsData {
	doorType: (null | KitchenDoorType) | null;
	/** @format int32 */
	drawers: number | null;
	facade: (null | KitchenFacadeStyle) | null;
	hinge: (null | FrontHinge) | null;
	oven: boolean | null;
	/** @format int32 */
	shelves: number | null;
	upper: (null | UpperFront) | null;
}

/** @format int32 */
export enum KitchenHandleStyle {
	Bar = 0,
	Knob = 1,
	Cutout = 2,
	None = 3,
}

/** @format int32 */
export enum KitchenLayout {
	Straight = 0,
	Corner = 1,
	U = 2,
}

export interface KitchenLengthsData {
	/** @format int32 */
	back: number;
	/** @format int32 */
	island: number;
	/** @format int32 */
	left: number;
	/** @format int32 */
	right: number;
}

/** @format int32 */
export enum KitchenModuleKind {
	Doors = 0,
	Drawers = 1,
	Sink = 2,
	Cooktop = 3,
	Oven = 4,
	Bottle = 5,
	Dishwasher = 6,
	Fridge = 7,
	Column = 8,
	Empty = 9,
}

export interface KitchenRunSectionsData {
	lower: KitchenSectionData[];
	upper: KitchenUpperSectionData[];
}

export interface KitchenSectionData {
	cells: KitchenCellData[] | null;
	/** @format int32 */
	depth: number | null;
	front: (null | KitchenFrontSettingsData) | null;
	/** @format int32 */
	height: number | null;
	id: string;
	kind: KitchenModuleKind;
	/** @format int32 */
	width: number;
}

/** @format int32 */
export enum KitchenSide {
	Left = 0,
	Right = 1,
}

/** @format int32 */
export enum KitchenUpperKind {
	Cabinet = 0,
	Hood = 1,
	Empty = 2,
}

export interface KitchenUpperSectionData {
	cells: KitchenCellData[] | null;
	/** @format int32 */
	depth: number | null;
	front: (null | KitchenFrontSettingsData) | null;
	/** @format int32 */
	height: number | null;
	id: string;
	kind: KitchenUpperKind;
	/** @format int32 */
	width: number;
}

export interface LoginRequest {
	email: string;
	password: string;
}

/** @format int32 */
export enum ModuleBase {
	Plinth = 0,
	Legs = 1,
	None = 2,
}

export interface ModuleData {
	base: ModuleBase;
	bodyColor: string;
	/** @format int32 */
	depth: number;
	/** @format int32 */
	drawers: number;
	/** @format int32 */
	elevation: number;
	frontColor: string;
	handle: ModuleHandle;
	/** @format int32 */
	height: number;
	sections: ModuleSectionData[];
	/** @format int32 */
	shelves: number;
	type: FurnitureModuleType;
	/** @format int32 */
	width: number;
}

/** @format int32 */
export enum ModuleHandle {
	Bar = 0,
	Knob = 1,
	None = 2,
}

export interface ModuleSectionData {
	fill: ModuleSectionFill;
	hinge: (null | FrontHinge) | null;
}

/** @format int32 */
export enum ModuleSectionFill {
	Open = 0,
	Doors = 1,
	Drawers = 2,
	DrawerDoors = 3,
}

export interface ProjectDataDto {
	data: JsonElement;
	description: string | null;
	/** @format uuid */
	id: string;
	isReadOnly: boolean;
	/** @format date-time */
	lastModifiedDate: string;
	name: string;
}

export interface ProjectDto {
	/** @format date-time */
	createdAt: string;
	description: string | null;
	/** @format uuid */
	id: string;
	isOwner: boolean;
	/** @format date-time */
	lastModifiedDate: string;
	name: string;
	sharedWithEmail: string | null;
	sharingMode: ProjectSharingMode;
	sharingPermission: ProjectSharingPermission;
}

/** @format int32 */
export enum ProjectSharingMode {
	Disabled = 0,
	Link = 1,
	Email = 2,
}

/** @format int32 */
export enum ProjectSharingPermission {
	View = 0,
	Edit = 1,
}

export interface RefreshTokenRequest {
	refreshToken: string;
}

export interface RegisterUserRequest {
	email: string;
	name: string | null;
	password: string;
}

export interface RequestPasswordResetRequest {
	email: string;
}

export interface ResetPasswordRequest {
	code: string;
	email: string;
	newPassword: string;
}

export interface SaveProjectDataRequest {
	data: JsonElement;
	/** @format date-time */
	lastModifiedDate: string;
}

export interface UpdateProjectRequest {
	description: string | null;
	name: string;
}

export interface UpdateProjectSharingRequest {
	email: string | null;
	sharingMode: ProjectSharingMode;
	sharingPermission: ProjectSharingPermission;
}

export interface UpdateUserEmailRequest {
	email: string;
}

export interface UpdateUserPasswordRequest {
	password: string;
}

export interface UpdateUserRequest {
	name: string | null;
}

export interface UpdateUserRoleRequest {
	role: UserRole;
}

/** @format int32 */
export enum UpperFront {
	Doors = 0,
	Lift = 1,
}

export interface UserDto {
	/** @format date-time */
	createdAt: string;
	email: string;
	/** @format uuid */
	id: string;
	isEmailConfirmed: boolean;
	name: string | null;
	role: UserRole;
}

/** @format int32 */
export enum UserRole {
	User = 0,
	Admin = 1,
}

export interface WardrobeCellData {
	direction: (null | WardrobeDoorDirection) | null;
	door: (null | WardrobeDoor) | null;
	fill: (null | WardrobeFill) | null;
	/** @format int32 */
	shelves: number | null;
}

export interface WardrobeCellGroupData {
	cells: string[];
	id: string;
}

export interface WardrobeData {
	cells: Record<string, WardrobeCellData>;
	/** @format int32 */
	columns: number;
	cornerSide: null | WardrobeSide;
	customColor: string | null;
	defaultDirection: null | WardrobeDoorDirection;
	defaultDoor: null | WardrobeDoor;
	/** @format int32 */
	depth: number;
	doorColor: WardrobeDoorColor;
	doorCustomColor: string;
	doorsVisible: boolean;
	finish: WardrobeFinish;
	groups: WardrobeCellGroupData[];
	/** @format int32 */
	height: number;
	layout: null | WardrobeLayout;
	/** @format int32 */
	leftColumns: number | null;
	/** @format int32 */
	leftLength: number | null;
	/** @format int32 */
	rightColumns: number | null;
	/** @format int32 */
	rightLength: number | null;
	/** @format int32 */
	rows: number;
	/** @format int32 */
	width: number;
}

/** @format int32 */
export enum WardrobeDoor {
	None = 0,
	Door = 1,
}

/** @format int32 */
export enum WardrobeDoorColor {
	Wood = 0,
	White = 1,
	Custom = 2,
}

/** @format int32 */
export enum WardrobeDoorDirection {
	Left = 0,
	Right = 1,
	Bottom = 2,
	Drawer = 3,
}

/** @format int32 */
export enum WardrobeFill {
	Empty = 0,
	Rail = 1,
}

/** @format int32 */
export enum WardrobeFinish {
	LightOak = 0,
	DarkWalnut = 1,
	Graphite = 2,
	White = 3,
	WoodTexture = 4,
	Custom = 5,
}

/** @format int32 */
export enum WardrobeLayout {
	Straight = 0,
	Corner = 1,
	U = 2,
}

/** @format int32 */
export enum WardrobeSide {
	Left = 0,
	Right = 1,
}
